<template>
	<onboarding class="bg">
		<Page>
			<template v-slot:content>
				<SafeAreaTop :showClose="false">
					<div class="container">
						<survey-title class="mt-7vh">Any reason you didn't subscribe?</survey-title>
						<SurveySelect
							type="multiple"
							:label="selectLabel"
							:options="options"
							:checkIcon="checkIcon"
							@change="onChange"
						/>
					</div>
				</SafeAreaTop>
			</template>
			<template v-slot:footer>
				<div class="container">
					<a v-if="!includePreferNotToAnswer" class="btn btn-link" href="" @click.prevent="close('Close')">
						Prefer not to answer
					</a>
					<a href="" @click.prevent="goNext" class="btn btn-primary btn-block" :class="{ disabled }">Send</a>
				</div>
			</template>
		</Page>
	</onboarding>
</template>

<script>
import Page from '@/components/Page.vue'
import SurveySelect from '@/components/SurveySelect.vue'
import SurveyTitle from './components/Title.vue'
import SafeAreaTop from '@/components/SafeAreaTop'
import Onboarding from '@/components/Onboarding.vue'
import { computed } from '@vue/reactivity'
import { useStore } from 'vuex'
import { inject, onMounted } from '@vue/runtime-core'
import useConfig from '@/composition/config.js'
import useApi from '@/composition/api.js'

export default {
	components: {
		Onboarding,
		Page,
		SurveySelect,
		SurveyTitle,
		SafeAreaTop
	},
	setup() {
		const store = useStore()
		const config = useConfig()
		const amplitude = inject('amplitude')
		const api = useApi()

		const includePreferNotToAnswer = computed(() =>
			config.get({ key: 'rejection_reason.includePreferNotToAnswer', default_value: false })
		)

		const options = computed(() => {
			const opts = [
				{
					label: "I don't understand how Simple will help me"
				},
				{
					label: "It's too expensive"
				},
				{
					label: "I don't understand the pricing"
				},
				{
					label: 'I use another app to track my nutrition'
				},
				{
					label: "I'm already a Premium member"
				},
				{
					label: 'Other'
				}
			]
			if (includePreferNotToAnswer.value) {
				opts.push({
					label: 'Prefer not to answer'
				})
			}
			return store.getters['survey/getProperty']({
				key: 'rejection_reason',
				options: opts
			})
		})

		const selected = computed(() => options.value.filter((option) => option.selected))
		const disabled = computed(() => selected.value.length == 0)

		const checkIcon = computed(() => config.get({ key: 'rejection_reason.checkIcon', default_value: 'check-circle' }))
		const selectLabel = computed(() =>
			config.get({ key: 'rejection_reason.selectLabel', default_value: 'You can select multiple answers' })
		)

		const onChange = (option) => {
			if (includePreferNotToAnswer.value) {
				if (option[option.length - 1] === 'Prefer not to answer') {
					store.commit('survey/update', {
						property: 'rejection_reason',
						value: ['Prefer not to answer']
					})
					return
				} else if (option.includes('Prefer not to answer')) {
					store.commit('survey/update', {
						property: 'rejection_reason',
						value: option.slice().filter((v) => v !== 'Prefer not to answer')
					})
					return
				}
				store.commit('survey/update', {
					property: 'rejection_reason',
					value: option
				})
			} else {
				store.commit('survey/update', {
					property: 'rejection_reason',
					value: option
				})
			}
		}

		const layout = computed(() => config.get({ key: 'layout', default_value: 'Workbooks' }))
		const close = (Status = 'Close') => {
			amplitude.logEvent('Survey - Close', {
				Layout: layout.value,
				From: store.getters['getSource'],
				Step: 'Survey',
				Status,
				Question: "Any reason you didn't subscribe?",
				Answers: selected.value.map((option) => option.label)
			})

			const reason = Status == 'Close' ? 'Prefer not to answer' : selected.value.map((option) => option.label)
			amplitude.logEvent(store.getters['getAmplitudeEventName']('UnsubscribeReasonScreenDone'), {
				reason
			})

			api.close()
		}

		const goNext = () => {
			if (includePreferNotToAnswer.value && selected.value.some((v) => v === 'Prefer not to answer')) {
				return close('Close')
			}
			close('Answer')
		}

		onMounted(() => {
			amplitude.logEvent(store.getters['getAmplitudeEventName']('UnsubscribeReasonScreen'), {
				options: options.value.map((option) => option.label)
			})
		})

		return {
			options,
			onChange,
			disabled,
			goNext,
			close,
			checkIcon,
			selectLabel,
			includePreferNotToAnswer
		}
	}
}
</script>

<style lang="scss" scoped>
.btn-link {
	color: #7d8bf7;
}
.bg {
	background: var(--rejection-reason-background);
}
</style>
